import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { PAYMENT_TERMS } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ViewVendor = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchVendor();
  }, [id]);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getVendor/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setVendor(data.vendor);
      } else {
        setErrorMessage(data.error || "Failed to fetch vendor");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage("Failed to fetch vendor");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentTermLabel = (termId) => {
    const term = PAYMENT_TERMS.find((t) => t.id === termId);
    return term ? term.name : termId || "N/A";
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
          colors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status || "active"}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Vendor not found</p>
        <button
          onClick={() => navigate("/Vendors")}
          className="mt-4 text-primary hover:underline"
        >
          Back to Vendors
        </button>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/Vendors")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">{vendor.name}</h2>
            <p className="text-sm text-gray-500">
              {vendor.vendorCode || "Vendor Details"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(vendor.status)}
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Contact Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Vendor Name</p>
              <p className="font-medium">{vendor.name}</p>
            </div>
            <div>
              <p className="text-gray-500">Contact Person</p>
              <p className="font-medium">{vendor.contactPerson || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-500">Email</p>
              <p className="font-medium">{vendor.email || "N/A"}</p>
            </div>
            <div>
              <p className="text-gray-500">Phone</p>
              <p className="font-medium">{vendor.phone || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Business Details
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Vendor Code</p>
              <p className="font-medium text-primary">
                {vendor.vendorCode || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Payment Terms</p>
              <p className="font-medium">
                {getPaymentTermLabel(vendor.paymentTerms)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Lead Time</p>
              <p className="font-medium">
                {vendor.leadTimeDays ? `${vendor.leadTimeDays} days` : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <p className="font-medium capitalize">{vendor.status || "active"}</p>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Address
          </h3>
          <p className="text-sm text-gray-700">{vendor.address || "No address provided"}</p>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Notes
          </h3>
          <p className="text-sm text-gray-700">{vendor.notes || "No notes"}</p>
        </div>

        {/* Record Info */}
        <div className="bg-white rounded-lg shadow p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
            Record Info
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Created</p>
              <p className="font-medium">
                {vendor.createdAt
                  ? new Date(vendor.createdAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Last Updated</p>
              <p className="font-medium">
                {vendor.updatedAt
                  ? new Date(vendor.updatedAt).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewVendor;
